import Hero from '../components/Hero';
import SectionHeading from '../components/SectionHeading';
import ImageWithText from '../components/ImageWithText';
import CallToAction from '../components/CallToAction';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const WhatItDoes = () => {
  return (
    <>
      <Hero 
        title="Simple Sheep Management Tools"
        subtitle="Discover how PedigreePro helps breeders manage their flocks more efficiently with powerful, easy-to-use tools."
        image="/images/kerry2.jpg"
      />
      
      <section className="section bg-white">
        <div className="container">
          <SectionHeading 
            title="Detailed Sheep Records"
            subtitle="PedigreePro records all the details of each sheep, including parents, pedigrees and offspring, all in one interactive space."
          />
          
          <div className="space-y-24">
            <ImageWithText
              image="/images/sheep_move.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              title="Movement Records"
              description="With continuing new and worrying diseases coming in play in the sheep industry, never has it been more important to keep accurate records of animal movements. Enter locations, fields and simple record movements, keeping a record of what animal is at what location. Imagine walking into a specific field, and knowing exactly what animals should be in there? "
            >
              <ul className="space-y-2">
                <FeatureItem text="Location of animals at a glance" />
                <FeatureItem text="List and export of all movement records" />
                <FeatureItem text="CPH numbers and other location details" />
                <FeatureItem text="Keep track of sale prices" />
                <FeatureItem text="Sort animals by location" />
              </ul>
            </ImageWithText>
            
            <ImageWithText
              image="/images/injection-lambs.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              title="Food Security"
              description="With all eyes on food security, these days it is highly important to keep records of every medical treatment each animal has been administered. PedigreePro has that covered, with individual treatments, selected animals or blanket dosing, it keeps those records and reminds of any withdrawal periods."
              reverse
            >
              <ul className="space-y-2">
                <FeatureItem text="Individual Treatments" />
                <FeatureItem text="Selected animal single entry" />
                <FeatureItem text="Blanket treatments" />
                <FeatureItem text="Withdrawal period monitoring" />
                <FeatureItem text="Medical records export for inspection" />
              </ul>
            </ImageWithText>
            
            <ImageWithText
              image="/images/part_flock_list.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              title="Simple Data Entry"
              description="Simple data entry of animals, where you can enter a number of different ID and Tag numbers, as well as Date of Birth, Colour, etc. You can also add ancestors to populate a family tree going back unlimited generations, as well as output animals for registration with breed societies."
              reverse
            >
              <ul className="space-y-2">
                <FeatureItem text="Sortable flock list" />
                <FeatureItem text="Interactive Offsping and pedigree module" />
                <FeatureItem text="Temporary tag recording for lambs" />
                <FeatureItem text="Simple flock stats" />
                <FeatureItem text="Inbreeding coefficient calculations" />
              </ul>
            </ImageWithText>
            
            <ImageWithText
              image="/images/spreadsheet1.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              title="Spreadsheet Reporting"
              description="You can output all your records at the push of a button, to an Excel Worksheet, for your own intergogation."
            >
              <ul className="space-y-2">
                <FeatureItem text="Industry standard tools" />
                <FeatureItem text="Export animals to regsiter" />
                <FeatureItem text="Health and medication reports" />
                <FeatureItem text="Movement records between dates" />
                <FeatureItem text="Active medical withdrawal periods" />
              </ul>
            </ImageWithText>
             <ImageWithText
              image="/images/spreadsheet1.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              title="Output registrations"
              description="Output animals to be registered at the click of a button. We are working with some breed societies to be able to accept this file by email."
              reverse
            >
              <ul className="space-y-2">
                <FeatureItem text="Output Animals to Register" />
                <FeatureItem text="Send file to breed societies" />
                <FeatureItem text="Use file to register lambs on other software" />
                <FeatureItem text="See lambs at a glance" />
                <FeatureItem text="Using industry standard Excel software" />
              </ul>
            </ImageWithText>
            <ImageWithText
              image="/images/beatrice_rhs_22.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              title="Show and Prize records"
              description="Keep track of what animals have been to which show, and the awards they have won."
              reverse
            >
              <ul className="space-y-2">
                <FeatureItem text="Prizes and Awards" />
                <FeatureItem text="Names of cups and trophies" />
                <FeatureItem text="Useful records when selling breeding stock" />
                <FeatureItem text="Tracking prize money won" />
                <FeatureItem text="List of show locations" />
              </ul>
            </ImageWithText>

            <ImageWithText
              image="/images/nippy.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              title="Matrilineal Family Lines"
              description="Ever wondered about how the great pioneer breeders like Robert Bakewell talked of female families and line breeding? They did it with pen and paper — PedigreePro does it in seconds. Select any ewe and instantly trace her complete matrilineal line back to the founding female of her family, generation by generation, with all descendants shown below. Export the full family tree to a beautifully formatted PDF to keep or share."
            >
              <ul className="space-y-2">
                <FeatureItem text="Full dam-line trace from any ewe back to her matriarch" />
                <FeatureItem text="All descendants displayed by generation" />
                <FeatureItem text="Males shown as family members but not propagating the line" />
                <FeatureItem text="Colour-coded gender indicators in the PDF output" />
                <FeatureItem text="One-click PDF export, named and dated automatically" />
              </ul>
            </ImageWithText>

            <ImageWithText
              image="/images/ryeland4.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              title="Inbreeding Check"
              description="One of the most powerful and unique tools in PedigreePro. Select any ram — including one you are thinking of buying — and the system instantly calculates Wright's inbreeding coefficient for potential offspring with every single owned ewe in your flock. Results are colour-coded from green (no shared ancestry) through yellow and orange to red (high risk — avoid). You can even import a prospective purchase's pedigree and test him before you commit to buying."
              reverse
            >
              <ul className="space-y-2">
                <FeatureItem text="Wright's path coefficient method — the gold standard" />
                <FeatureItem text="Instant results for every ewe against any ram" />
                <FeatureItem text="Colour-coded risk ratings: green, yellow, orange, red" />
                <FeatureItem text="Test prospective ram purchases before buying" />
                <FeatureItem text="Double-click any result to export a test-mating pedigree PDF" />
                <FeatureItem text="Export the full inbreeding report to PDF" />
              </ul>
            </ImageWithText>

            <ImageWithText
              image="/images/flock1.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              title="Family Groups"
              description="Organise your animals into named family groups — the Dot family, the Clara family, the Bella family — reflecting the maternal lines that define your breeding programme. Browse every owned animal in a family at a glance, with their sire, dam, gender and year of birth. Export all your family data in a single Excel workbook, with a separate tab for each family, at the click of a button."
            >
              <ul className="space-y-2">
                <FeatureItem text="Create and manage named family groups" />
                <FeatureItem text="Assign animals to families from the edit screen" />
                <FeatureItem text="Browse all owned animals in any family" />
                <FeatureItem text="See sire, dam, gender and birth year at a glance" />
                <FeatureItem text="One-click Excel export — all families, tabbed by name" />
              </ul>
            </ImageWithText>

            <ImageWithText
              image="/images/flock3.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              title="Tupping Records"
              description="New this autumn: PedigreePro now includes a tupping feature. Enter the tupping details for each individual ewe (the ram used and the date) and PedigreePro will output your tupping and lambing calendar, so you know exactly when each ewe is due. Yet another improvement to make flock management easier."
              reverse
            >
              <ul className="space-y-2">
                <FeatureItem text="Record tupping dates for each individual ewe" />
                <FeatureItem text="Record which ram served each ewe" />
                <FeatureItem text="Automatic lambing due dates" />
                <FeatureItem text="Output your tupping calendar" />
                <FeatureItem text="Plan your lambing shed in advance" />
              </ul>
            </ImageWithText>

            <ImageWithText
              image="/images/lamb_weigh.jpg"
              title="Weight Recording"
              description="Record every weight against the animal: birth, weaning, slaughter or any other date you choose, as many as you like. Add a condition score and, for slaughter weights, a carcass grade. PedigreePro works out each animal's age, gain and daily gain from the previous weighing, so you can see exactly how your lambs are growing. Work in kilograms or pounds, whichever you prefer."
            >
              <ul className="space-y-2">
                <FeatureItem text="Birth, weaning, slaughter and other weights" />
                <FeatureItem text="As many weights per animal as you need" />
                <FeatureItem text="Condition score from 1 to 5 in half steps" />
                <FeatureItem text="Carcass grade for slaughter weights" />
                <FeatureItem text="Age, gain and daily gain worked out automatically" />
                <FeatureItem text="Kilograms or pounds, set in your profile" />
              </ul>
            </ImageWithText>

            <ImageWithText
              image="/images/valais-blacknose2.jpg"
              title="Weigh the Flock in the Shed"
              description="Weighing a whole pen? Open the Weights screen, pick the date and the type of weight, then narrow the list by location, year of birth or gender. Type each weight straight down the list, with the Enter key jumping to the next animal, and save the whole group in one go. Every column can be sorted, and the screen is laid out to work on a phone or tablet out in the shed."
              reverse
            >
              <ul className="space-y-2">
                <FeatureItem text="Filter by location, year of birth or gender" />
                <FeatureItem text="Sort the list by any column" />
                <FeatureItem text="Type weights straight down the list" />
                <FeatureItem text="Enter condition scores and slaughter grades at the same time" />
                <FeatureItem text="Warning if a weight is lower than last time" />
                <FeatureItem text="Save the whole group in one go" />
              </ul>
            </ImageWithText>

            <ImageWithText
              image="/images/flock2.jpg"
              title="Weight Reports and Profit Analysis"
              description="Export every weighing between two dates to Excel, with age, gain, daily gain, dam, sire, location and sale price, ready to sort and filter in your own spreadsheet. Premium subscribers also get the Weight & Profit Analysis workbook: for each lambing season it shows which ewes and rams are producing your best lambs, comparing each lamb fairly with others of the same sex and birth type. Importing weights directly from EID stick readers is coming soon."
            >
              <ul className="space-y-2">
                <FeatureItem text="Lamb weights export between any two dates, on every plan" />
                <FeatureItem text="Premium: ewe scorecard with kg of lamb weaned per ewe" />
                <FeatureItem text="Premium: barren and below-average ewes flagged" />
                <FeatureItem text="Premium: ram progeny results, with the number of lambs behind each" />
                <FeatureItem text="Premium: slaughter grades measured against your target" />
                <FeatureItem text="Premium: revenue from recorded sales, plus a check for missing records" />
              </ul>
            </ImageWithText>
          </div>
        </div>
      </section>
      
      <section className="section bg-neutral-50">
        <div className="container">
          <SectionHeading 
            title="Why Choose PedigreePro?"
            subtitle="PedigreePro stands out from other livestock management software with its simplicity and affordability."
          />
          
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="font-sans font-semibold text-xl mb-3">FREE software</h3>
              <p className="text-neutral-600">
                Where else can you get free dedicated software designed by the very people who use it themselves?
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="font-sans font-semibold text-xl mb-3">Cloud-Based Access</h3>
              <p className="text-neutral-600">
                Access your flock data from anywhere, on any device, with secure cloud-based storage that ensures your information is always backed up and available.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="font-sans font-semibold text-xl mb-3">User-Friendly Interface</h3>
              <p className="text-neutral-600">
                Designed with simplicity in mind, PedigreePro works and displays best in a PC environment, but it also runs well on tablets and smart phones, making it transferable to the field and lambing shed. 
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="font-sans font-semibold text-xl mb-3">Regular Updates</h3>
              <p className="text-neutral-600">
                We continually improve PedigreePro with new features and enhancements based on user feedback and industry developments.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="font-sans font-semibold text-xl mb-3">Dedicated Support</h3>
              <p className="text-neutral-600">
                Admin support at the touch of a button, as well as a unique on screen assistant.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="font-sans font-semibold text-xl mb-3">Data Security</h3>
              <p className="text-neutral-600">
                Your valuable flock data is protected with enterprise-grade security measures, regular backups, and strict privacy controls.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-primary">
              <h3 className="font-sans font-semibold text-xl mb-3">Unique Pedigree Tools</h3>
              <p className="text-neutral-600">
                No other sheep management software offers matrilineal family line tracing, named family groups, and instant Wright's inbreeding coefficient checks all in one place. Tools that were once the preserve of breed society geneticists are now at your fingertips.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-primary">
              <h3 className="font-sans font-semibold text-xl mb-3">Buy with Confidence</h3>
              <p className="text-neutral-600">
                Import a prospective ram's pedigree certificate in seconds and run an inbreeding check against your whole flock before you part with a penny. Make informed breeding decisions backed by science, not guesswork.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      <CallToAction 
        title="Experience PedigreePro Today"
        description="Join thousands of sheep breeders who have transformed their flock management with PedigreePro."
        buttonText="Launch PedigreePro App"
        buttonLink="https://pedigreepro.netlify.app/"
        secondaryButtonText="View Subscription Plans"
        secondaryButtonLink="/subscription"
      />
    </>
  );
};

const FeatureItem = ({ text }: { text: string }) => (
  <li className="flex items-start gap-2">
    <span className="mt-1 text-success">
      <Check size={16} />
    </span>
    <span>{text}</span>
  </li>
);

export default WhatItDoes;